import Skeleton from '@mui/material/Skeleton';
import { Size } from '@ps/web-core-enums';
import type { ClubSeason } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import { StarsDropDown, useScreenSize } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import type { RootState } from '@swf/core/store';

import { useHomeGamesWidget } from '../../../model';
import { formatSeasonName } from '../../../utils/formatSeasonName';
import type { MenuOption } from '../../DropdownMenu/DropdownMenu';

import styles from './SeasonsDropdown.scss';

export interface Props {
  loading?: boolean;
  onSelect?: (option: MenuOption) => void;
  value?: string;
}
const Form = createForm();

const getSeasonsOptions = (seasons: ClubSeason[]) =>
  seasons.map(({ seasonId, seasonName }) => ({
    text: seasonName ? formatSeasonName(seasonName) : '',
    value: `${seasonId}`
  }));

const Dropdown: React.FC<Props> = ({ onSelect, value }) => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(Size.XXSmall);
  const { widgetSelect } = useHomeGamesWidget();
  const seasons = useSelector((rootState: RootState) =>
    widgetSelect.clubSeasons(rootState, clubId)
  );
  const { clubSeasons: loading } = useSelector(widgetSelect.loading);

  const noSeasons = isEmpty(seasons);
  const options = useMemo(
    () => (noSeasons ? [] : getSeasonsOptions(seasons)),
    [noSeasons, seasons]
  );
  const optionFromURL = options.find((option) => option.value === value);
  const valueProps = value && optionFromURL ? { value, defaultOption: optionFromURL } : {};

  if (loading) {
    return isMobile ? (
      <Skeleton
        className={styles.seasonsDropdownSkeleton}
        variant="circular"
        height={45}
        width={45}
      />
    ) : (
      <Skeleton
        className={styles.seasonsDropdownSkeleton}
        variant="rectangular"
        height={45}
        width={200}
      />
    );
  }

  if (noSeasons) return null;

  return (
    <StarsDropDown
      hideLabel
      label="Seasons"
      id="seasonsDropdown"
      data-testid="seasons-dropdown"
      options={options}
      className={classNames(styles.seasonsDropdown, {
        [styles.hiddenOnMobile]: options?.length === 1
      })}
      defaultOption={options[0]}
      onSelect={onSelect}
      disabled={loading || options.length === 1}
      {...valueProps}
    />
  );
};

const SeasonsDropdown: React.FC<Props> = ({ onSelect, value }) => (
  <Form
    className={styles.form}
    onSubmit={() => undefined}
    render={() => <Dropdown onSelect={onSelect} value={value} />}
  />
);

export default SeasonsDropdown;
